import { useEffect } from 'react';
import { useSystemConfiguration } from '@/api/system-configuration/use-system-configuration';
import { usePageTitleParts } from '@/contexts/PageTitleContext';

/**
 * Single writer for document.title and the PWA name meta tags.
 *
 * Title format: "<page> · <tenant> <area>" — e.g. "Customers · demo CRM".
 * The page part comes from PageHeader, the area from the mounted layout.
 */
export const PageTitleSetter: React.FC = () => {
    const { data: config } = useSystemConfiguration();
    const { area, pageTitle } = usePageTitleParts();

    useEffect(() => {
        const systemName = config?.system_name || 'Quantix';
        const suffix = area ? `${systemName} ${area}` : systemName;

        document.title = pageTitle ? `${pageTitle} · ${suffix}` : suffix;

        // PWA home-screen label (iOS) always uses the bare tenant name.
        let applicationNameMeta = document.querySelector('meta[name="application-name"]');
        if (!applicationNameMeta) {
            applicationNameMeta = document.createElement('meta');
            applicationNameMeta.setAttribute('name', 'application-name');
            document.head.appendChild(applicationNameMeta);
        }
        applicationNameMeta.setAttribute('content', systemName);

        let appleWebAppTitleMeta = document.querySelector(
            'meta[name="apple-mobile-web-app-title"]'
        );
        if (!appleWebAppTitleMeta) {
            appleWebAppTitleMeta = document.createElement('meta');
            appleWebAppTitleMeta.setAttribute('name', 'apple-mobile-web-app-title');
            document.head.appendChild(appleWebAppTitleMeta);
        }
        appleWebAppTitleMeta.setAttribute('content', systemName);
    }, [config?.system_name, area, pageTitle]);

    return null;
};
